import { I18n } from '@calendly/i18n';
import { Loader } from '@calendly/ui/components/loader';
import classNames from 'classnames';
import { useContext, useState, forwardRef } from 'react';
import { Invitee } from './invitee';
import { ShowMoreButton } from './show_more_button';
import { MeetingDetailDependenciesContext } from '../../../meeting_detail_context';
const INVITEES_TO_SHOW = 10;
const emptyContainerCss = "e1nxyrgn";
const EmptyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyContainerCss, props.className)} />;
});
const InviteesList = ({
  event,
  invitees,
  isFetching,
  onNextPage,
  readonly
}) => {
  const [isExpanded, setExpanded] = useState(invitees.results.length <= INVITEES_TO_SHOW);
  const inviteesToShow = isExpanded ? invitees.results : invitees.results.slice(0, INVITEES_TO_SHOW);
  const moreInviteesCount = invitees.pagination.totalCount - inviteesToShow.length;
  const showMoreButtonText = event.past ? String(I18n.t('publisher.meetings.invitees.view_more_attendees', {
    count: moreInviteesCount
  })) : String(I18n.t('publisher.meetings.invitees.view_more_invitees', {
    count: moreInviteesCount
  }));
  const handleShowMore = () => {
    if (!isExpanded) {
      setExpanded(true);
    } else {
      onNextPage();
    }
  };
  return <div>
      {inviteesToShow.map(invitee => <Invitee key={invitee.id} event={event} invitee={invitee} readonly={readonly} />)}
      {(!isExpanded || invitees.pagination.nextPage) && <ShowMoreButton disabled={isFetching} event={event} expand={handleShowMore}>
          {isFetching ? <Loader /> : showMoreButtonText}
        </ShowMoreButton>}
    </div>;
};
export const GroupEvent = ({
  event,
  readonly
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies?.useGetInviteesQuery) {
    throw new Error('GroupEvent must be wrapped in a MeetingDetailDependenciesProvider with useGetInviteesQuery');
  }
  const [page, setPage] = useState(1);
  // Handle both id and uuid - API accepts UUIDs for eventId
  const eventId = event.id || event.uuid;

  // Check if event has invitees data available (for fallback)
  const eventInvitees = event.invitees || [];
  const hasEventInvitees = eventInvitees.length > 0;

  // Always try API call first, but fall back to event invitees if it fails
  const {
    data: invitees,
    isLoading,
    isFetching,
    isError,
    error
  } = dependencies.useGetInviteesQuery({
    eventId,
    page
  }, {
    skip: !eventId
  });
  const handleNextPage = () => {
    setPage(prevPage => prevPage + 1);
  };

  // If eventId is missing, don't show error - just return null
  // The widget should handle this case at a higher level
  if (!eventId) {
    return null;
  }

  // If API call failed with policy error but we have event invitees, use those instead
  if (isError && error?.type === 'policy' && hasEventInvitees) {
    // Map event invitees to match API format (ensure id field exists, use uuid if id is missing)
    const mappedInvitees = eventInvitees.map(invitee => ({
      ...invitee,
      id: invitee.id || invitee.uuid
    }));
    const inviteesFromEvent = {
      results: mappedInvitees,
      pagination: {
        totalCount: mappedInvitees.length,
        currentPage: 1,
        nextPage: false
      }
    };
    return <InviteesList event={event} invitees={inviteesFromEvent} isFetching={false} onNextPage={handleNextPage} readonly={readonly} />;
  }
  if (isLoading) {
    return <EmptyContainer data-testid="loader">
        <Loader />
      </EmptyContainer>;
  }
  if (isError) {
    // Log the error for debugging
    console.error('Error fetching invitees:', error);
    return <EmptyContainer>
        {I18n.t('errors.default_no_action')}
      </EmptyContainer>;
  }
  if (!invitees) {
    return null;
  }

  // Normalize to { results, pagination } shape (API returns this; mocks may return [] or plain array)
  let normalizedInvitees = Array.isArray(invitees) ? {
    results: invitees,
    pagination: {
      totalCount: invitees.length,
      currentPage: 1,
      nextPage: false
    }
  } : {
    results: invitees.results ?? [],
    pagination: invitees.pagination ?? {
      totalCount: (invitees.results || []).length,
      currentPage: 1,
      nextPage: false
    }
  };

  // When API returns no results but event has invitees (e.g. Storybook, preloaded data), use those
  if (normalizedInvitees.results.length === 0 && hasEventInvitees) {
    const mapped = eventInvitees.map(inv => ({
      ...inv,
      id: inv.id || inv.uuid
    }));
    normalizedInvitees = {
      results: mapped,
      pagination: {
        totalCount: mapped.length,
        currentPage: 1,
        nextPage: false
      }
    };
  }
  return <InviteesList event={event} invitees={normalizedInvitees} isFetching={isFetching} onNextPage={handleNextPage} readonly={readonly} />;
};

require("./group_event.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./group_event.js");